import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import BarcodeInput from 'components/field/BarcodeInput';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import { ShipmentStatus } from 'components/field/Status';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Authorization from 'components/global/Authorization';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import ProductImage, { DCLogo } from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';

import PackingForm from 'pages/WarehouseUser/WHPacking/PackingForm/PackingForm';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, PRODUCTS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const PackingPilotForm = NiceModal.create(({ modalName, mode }) => {
	const axiosPrivate = useAxiosPrivate();
	const [items, setItems] = useState([]);
	const [orders, setOrders] = useState([]);
	const [packingStation, setPackingStation] = useState(null);
	const [warehouseID, setWarehouseID] = useState(null);
	const [waveNumber, setWaveNumber] = useState(null);
	const [loadingItem, setLoadingItem] = useState(false);
	const [loadingOrders, setLoadingOrders] = useState(false);

	const { closeModal } = useModalRouter();
	const { showError } = useAlert();
	const { getLocaleDateWithTime } = useLocale();

	const user = useSelector((state) => state?.user);

	const handleCloseModal = () => closeModal();

	const binAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const ordersWaveNumbers = useMemo(() => [...new Set(orders?.map((order) => order.WaveNumber))], [orders]);

	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];

	const fetchData = (abortController) => {
		setLoadingOrders(true);

		axiosPrivate
			.post(
				ADVANCE_WMS_MODULE.GET_PACK_LIST_BY_ITEMS,
				{
					items: items.map((item) => ({ sku: item.SKU, quantity: item.qty })),
					...{
						filter: packingStation
							? {
									packingStation: {
										type: 'eq',
										value: packingStation
									}
								}
							: {},
						whIds: [warehouseID],
						waveNumber: waveNumber ? waveNumber : undefined
					}
				},
				{ signal: abortController?.signal }
			)
			.then((response) => {
				if (response.data.success) {
					setOrders(response.data.items);
				} else {
					showError({
						message: response.data.errorMsg
					});
				}
				setLoadingOrders(false);
			})
			.catch((error) => {
				if (!abortController?.signal.aborted) {
					AppLogger.error(error);
					showError({
						message: error.code
					});
					setItems([]);
					setLoadingOrders(false);
				}
			});
	};

	const selectOrder = (order) => {
		let notFound = false;
		order.items.forEach((item) => {
			const itemAdded = items.find((a) => a.SKU === item.sku);
			if (!itemAdded || itemAdded.qty < item.quantity) {
				notFound = true;
			}
		});
		if (notFound) {
			NiceModal.show(PackingForm, {
				id: order?.id.toString(),
				initialPicks: items,
				onDone: fetchData,
				fetchData
			}).catch(() => fetchData());
			return;
		}
		NiceModal.show(PackingForm, {
			id: order?.id.toString(),
			initialTab: 1,
			onDone: fetchData,
			fetchData
		}).catch(() => fetchData());
	};

	const itemColumns = useMemo(
		() => [
			{
				header: 'nameAndSku',
				renderCell: ({ row }) => <ProductImage data={row.original} sku={row.original.SKU} />,
				showOnMobile: true
			},
			{
				header: 'barcode',
				accessorKey: 'barcode'
			},
			{
				header: 'quantity',
				accessorKey: 'qty',
				renderCell: ({ row }) => (
					<IntegerInput
						value={row.original.qty}
						setValue={(value) =>
							setItems((prev) => {
								prev[row.index].qty = value;
								return [...prev];
							})
						}
					/>
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[items]
	);

	const orderColumns = useMemo(
		() => [
			{
				header: 'DC',
				renderCell: ({ row }) => <DCLogo data={{ deliveryCompanyLogo: row.original.deliveryCompLogo }} />,
				maxSize: 50,
				showOnMobile: true
			},
			{
				header: 'orderID',
				accessorKey: 'orderID',
				showOnMobile: true
			},
			{
				header: 'shipmentNumber',
				accessorKey: 'shipmentNumber'
			},
			{
				header: 'orderDate',
				accessorKey: 'orderDate',
				renderCell: ({ row }) => <Typography>{getLocaleDateWithTime(row.original.orderDate)}</Typography>
			},
			{
				header: 'matchRate',
				accessorKey: 'match_rate',
				renderCell: ({ row }) => (
					<Typography
						color={
							(row.index === 0 || row.original.match_rate === 1) && row.original.match_rate !== 0
								? 'var(--blue)'
								: 'var(--black)'
						}>
						{`${row.original.match_rate * 100}%`}
					</Typography>
				)
			},
			{
				header: 'status',
				accessorKey: 'status',
				renderCell: ({ row }) => (
					<ShipmentStatus
						value={row.original.status}
						data={{ ID: row.original.id, orderID: row.original.orderID }}
					/>
				)
			},
			{
				header: 'action',
				renderCell: ({ row }) => <Button label="selectOrder" onClick={() => selectOrder(row.original)} />
			}
		],
		[orders, items]
	);

	useEffect(() => {
		if (warehouseID === null) {
			return;
		}

		const abortController = new AbortController();
		fetchData(abortController);

		return () => abortController && abortController.abort();
	}, [items, packingStation, warehouseID, waveNumber]);

	const checkBarcodeOrSKU = async (searchVal) => {
		try {
			const response = await axiosPrivate.post(PRODUCTS.GET_PRODUCT, { searchVal });
			if (response.data) {
				const found = items.findIndex((a) => a.SKU === response.data.SKU);
				if (found === -1) {
					setItems((prev) => [...prev, { ...response.data, qty: 1 }]);
				} else {
					setItems((prev) => {
						prev[found].qty += 1;
						return [...prev];
					});
				}
			} else {
				throw new Error('barcodeNotFound');
			}
		} catch {
			showError({
				message: 'barcodeNotFound'
			});
		}
	};

	const checkOrder = async (barcode) => {
		if (orders.length > 0) {
			const found = orders.findIndex((a) => a.orderID === barcode || a.shipmentNumber === barcode);
			if (found !== -1) {
				selectOrder(orders[found]);
			} else {
				checkBarcodeOrSKU(barcode);
			}
		} else {
			checkBarcodeOrSKU(barcode);
		}
	};

	const handleScan = async (barcode) => {
		setLoadingItem(true);
		await checkOrder(barcode);
		setLoadingItem(false);
	};

	return (
		<Modal
			fullwidth
			maxWidth="xx-large"
			name={modalName}
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
					}}
					onClose={close}
					label={mode === 'edit' ? 'packingPilot' : mode === 'view' ? 'viewPackingPilot' : 'packingPilot'}
				/>
			)}>
			<ModalTitle>
				<Typography variant="p1">packingPilot</Typography>
			</ModalTitle>
			<ModalContent>
				<Form>
					<FormItem colSpan={{ md: 6 }} label="warehouse">
						<AutoComplete
							name="warehouseID"
							autoCompleteName={
								user.userType === 'companyAdmin'
									? AUTOCOMPLETE.WAREHOUSES
									: user.userType === 'warehouseUser'
										? AUTOCOMPLETE.WAREHOUSES_WAREHOUSE_USER_BASED
										: null
							}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							value={warehouseID}
							setValue={(value) => {
								const warehouseValue = value?.ID ?? null;
								setWarehouseID(warehouseValue, {
									shouldValidate: true,
									shouldDirty: true
								});
								setPackingStation(null);
								setWaveNumber(null);
							}}
							filterObject={{
								userID: user.ID
							}}
						/>
					</FormItem>

					<Authorization path="WMS_BinTracking">
						<FormItem label="waveNumber" colSpan={{ md: 6 }}>
							<EnumSelect
								placeholder="Search"
								name="wave"
								disabled={!warehouseID}
								data={ordersWaveNumbers}
								value={waveNumber}
								setValue={setWaveNumber}
							/>
						</FormItem>
					</Authorization>

					<Authorization path="WMS_BinTracking">
						<FormItem label="packingStation" colSpan={{ md: 6 }}>
							<AutoComplete
								autoCompleteName={AUTOCOMPLETE.PACKING_BINS_BY_WAREHOUSE}
								setValue={(value) => {
									setPackingStation(value?.ID);
								}}
								disabled={!warehouseID}
								value={packingStation}
								columns={binAutocompleteColumns}
								selectionLabelAccessor={['Name']}
								filterObject={{
									warehouseID
								}}
							/>
						</FormItem>
					</Authorization>

					<FormItem label="scanBarcode" colSpan={{ md: 12 }}>
						<BarcodeInput onChange={handleScan} loading={loadingItem} />
					</FormItem>

					<FormItem>
						<Table
							data={items}
							columns={itemColumns}
							deletableLastRow={true}
							noRecordsFound={<></>}
							onRowDelete={(a) =>
								setItems((prev) => {
									prev.splice(a, 1);
									return [...prev];
								})
							}
						/>
					</FormItem>

					{warehouseID && (
						<FormItem>
							<Table
								data={orders}
								columns={orderColumns}
								noRecordsFound={
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											gap: '1rem',
											marginTop: '3rem',
											marginBottom: '1rem'
										}}>
										<Typography variant="p1" fontWeight="500">
											sms.notFoundTitle
										</Typography>
										<Typography variant="p2" color="var(--content-2)">
											noRecordMatchOrder
										</Typography>
									</div>
								}
								loading={loadingOrders}
							/>
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
			</ModalActions>
		</Modal>
	);
});

export default PackingPilotForm;
